import { Stack } from '@mui/material';
import * as R from 'ramda';
import { memo } from 'react';
import { isTouchEnabled } from '../../../../../../../Theme';
import useWidth from '../../../../../../../components/Hooks/useWidth';
import ProductGridInput from '../../../../../../../components/ProductGridInput/ProductGridInput';
import Typography from '../../../../../../../components/Typography/Typography';
import useTeceoStyles from '../../../../../../../useTeceoStyles';
import OrderProductPriceV2 from '../../../../../../orders/products/product/components/OrderProductPriceV2';
import useOrderProductGridStyles from '../../../../../../orders/styles/useOrderProductGrid.styles';
import { AvailabilityEnum } from '../../../../../ProductTypes';
import ProductDetailsAddOrderButtonV2 from '../../../../ProductDetailsAddOrderButtonV2';
import { CombinationButtonStatusEnum } from '../../../../hooks/useCombination';
import { useProductLock } from '../../../../hooks/useProductLock';
import { ClosedKitCombination } from '../../../../types/kit-combinations.types';
import { useCombinationLineStyles } from '../../../CombinationLine/components/CombinationLine/styles/combinationLineStyles.styles';
import { getCombinationWithImageLineHeight } from '../../../CombinationLine/utils/combinationLine.utils';
import { StackKit } from '../../types/kitDetailsList.types';
import CombinationItemImage from '../CombinationItemImage';
import useKitDetailsCombination from './hooks/useKitDetailsCombination';
import { useKitDetailsCombinationStyles } from './styles/useKitDetailsCombination.styles';

export interface KitDetailsCombinationProps {
  errorVersion: number;
  combination: ClosedKitCombination;
  isOfflineMode: boolean;
  pushItem: (item: StackKit) => void;
  refetchKitRelations: (kitStack: StackKit[]) => void;
  disableGrid?: boolean;
}

const KitDetailsCombination = ({
  combination,
  disableGrid,
  errorVersion,
  isOfflineMode,
  pushItem,
  refetchKitRelations,
}: KitDetailsCombinationProps) => {
  const { mobileWidth } = useWidth();
  const { getLimit } = useProductLock();
  const { classes } = useKitDetailsCombinationStyles();
  const { classes: teceoClasses } = useTeceoStyles();
  const { classes: itemClasses, cx } = useCombinationLineStyles({
    gridScrollerClassName: teceoClasses.hideScrollbar,
    height: getCombinationWithImageLineHeight(mobileWidth),
  });
  const { classes: orderProductGridStyles } = useOrderProductGridStyles({
    hasTouchGrid: isTouchEnabled,
  });

  const {
    grid,
    kitGridRef,
    gridToPrices,
    gridButtonStatus,
    handleOnChangeGrid,
    handleRemoveKitFromOrder,
  } = useKitDetailsCombination({
    combination,
    errorVersion,
    pushItem,
    refetchKitRelations,
    isOfflineMode,
  });

  const disableAddKit =
    disableGrid || (!R.isNil(grid.availableAmount) && grid.availableAmount <= 0);

  const renderPricesAndActionsSection = () => (
    <div className={classes.pricesAndActionsButtonContainer}>
      <OrderProductPriceV2
        grid={gridToPrices}
        partsQuantity={combination.orderQuantity ?? 0}
        total={(combination.orderQuantity ?? 0) * (combination.kitWholesalePrice ?? 0)}
      />
      <ProductDetailsAddOrderButtonV2
        gridButtonStatus={
          gridButtonStatus || { itemQuantity: 0, type: CombinationButtonStatusEnum.ADD }
        }
        onAdd={() => {
          handleOnChangeGrid(1);
        }}
        onRemove={handleRemoveKitFromOrder}
        disabled={disableAddKit}
      />
    </div>
  );

  return (
    <div
      className={cx({
        [orderProductGridStyles.root]: true,
        [itemClasses.root]: true,
        [itemClasses.combinationWithImagePadding]: true,
      })}
    >
      <div className={classes.root}>
        <Stack
          direction="row"
          sx={{
            width: mobileWidth ? '100%' : 'auto',
          }}
        >
          <CombinationItemImage imageUrl={combination.kitImageUrl} />

          <div className={classes.infoSection}>
            <Typography variant="body2-medium" alignSelf="flex-start">
              {combination.kitName}
            </Typography>
            <Typography variant="body2-regular" alignSelf="flex-start">
              {combination.kitCode}
            </Typography>
          </div>

          {mobileWidth && renderPricesAndActionsSection()}
        </Stack>

        <div>
          <ProductGridInput
            gridItem={grid}
            showStock={true}
            firstGridRef={kitGridRef}
            stock={grid.availableAmount ?? null}
            productColorAvailability={AvailabilityEnum.EMPTY}
            disabled={disableAddKit}
            onChangeGrid={({ value }) => {
              handleOnChangeGrid(value);
            }}
            maxValue={getLimit({
              availableAmount: grid.availableAmount,
              availability: AvailabilityEnum.READY_FOR_DELIVERY,
            })}
            inputProps={{
              min: 1,
            }}
          />
        </div>

        {!mobileWidth && renderPricesAndActionsSection()}
      </div>
    </div>
  );
};

export default memo(KitDetailsCombination);
